import { isBotId, type MemberRef, type Replay } from "@69ly/shared";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar } from "../components/Avatar.tsx";
import { Cs2Icon, WeaponIcon } from "../demo/cs2-icons.tsx";
import { inventoryAt, type PlayerView, type RoundIndex, type ScoreRow } from "../demo/scene.ts";
import { itemSlot } from "../demo/weapons.ts";

/**
 * The viewer's HUD over the map, as the game's own spectator view: a roster per side
 * (health, armour, what each player holds and carries, money, K/D), the kill feed, and
 * the speed menu of the transport bar.
 */

const SIDE_CLASS = { t: "side-t", ct: "side-ct" } as const;
const money = (n: number) => `$${n.toLocaleString("en-US")}`;

export function TeamRoster({
  replay,
  team,
  side,
  name,
  views,
  rows,
  tick,
  numbers,
  memberOf,
  followed,
  onFollow,
}: {
  replay: Replay;
  team: 0 | 1;
  side: "t" | "ct";
  name: string;
  views: ReadonlyMap<number, PlayerView>;
  rows: ScoreRow[];
  tick: number;
  numbers: number[];
  memberOf: (i: number) => MemberRef | null;
  followed: number | null;
  onFollow: (i: number) => void;
}) {
  const { t } = useTranslation();
  // A bot standing in for a player who left is listed only while it plays.
  const members = replay.players.map((p, i) => ({ p, i })).filter((x) => x.p.team === team && (!isBotId(x.p.steamId) || views.get(x.i)?.alive));
  const alive = members.filter(({ i }) => views.get(i)?.alive).length;
  const bank = members.reduce((n, { i }) => n + (views.get(i)?.money ?? 0), 0);
  return (
    <section
      className={`hud-roster ${SIDE_CLASS[side]}`}
      aria-label={t("demos.hud.roster", {
        side: t(`live.sideShort.${side}`),
        team: name,
      })}
    >
      <header className="hud-roster-head">
        <span className={`live-side ${SIDE_CLASS[side]}`}>{t(`live.sideShort.${side}`)}</span>
        <b className="hud-team-name">{name}</b>
        <span className="hud-team-meta mono">
          <span title={t("demos.hud.aliveHelp")}>{t("demos.hud.alive", { n: alive, of: members.length })}</span>
          <span title={t("demos.hud.bankHelp")}>{money(bank)}</span>
        </span>
      </header>
      <ul className="hud-players">
        {members.map(({ p, i }) => {
          const v = views.get(i);
          const isAlive = !!v?.alive;
          const items = isAlive ? (inventoryAt(replay, i, tick) ?? (v?.weapon ? [v.weapon] : [])) : [];
          const shown = items.filter((w) => itemSlot(w) !== "knife" && w !== "c4");
          const row = rows[i];
          const member = memberOf(i);
          return (
            <li key={i} className={`hud-player${isAlive ? "" : " is-dead"}${followed === i ? " is-followed" : ""}${v && v.blind > 0 ? " is-blind" : ""}`}>
              <button type="button" className="hud-player-button" aria-pressed={followed === i} onClick={() => onFollow(i)} title={t("demos.viewer.follow", { name: p.name })}>
                <span className="hud-line">
                  <span className={`demo-num ${SIDE_CLASS[side]}`}>{numbers[i]}</span>
                  {member && <Avatar name={member.name} url={member.avatarUrl} size={18} />}
                  <span className="hud-name">{p.name}</span>
                  {isAlive && v!.weapon && <WeaponIcon weapon={v!.weapon} className="hud-active" />}
                  <span className="hud-money mono">{money(v?.money ?? 0)}</span>
                </span>
                <span className="hud-line">
                  <span className="hud-hp" role="img" aria-label={isAlive ? t("demos.viewer.hp", { hp: v!.hp }) : t("demos.viewer.dead")}>
                    <span className={`hud-hp-fill${v && v.hp <= 20 ? " is-low" : ""}`} style={{ width: `${isAlive ? v!.hp : 0}%` }} />
                    <span className="hud-hp-value mono">{isAlive ? v!.hp : t("demos.hud.deadShort")}</span>
                  </span>
                  {isAlive && v!.armor > 0 && (
                    <Cs2Icon name={v!.helmet ? "assaultsuit" : "kevlar"} label={t(v!.helmet ? "demos.hud.armorHelmet" : "demos.hud.armor", { n: v!.armor })} className="hud-gear" />
                  )}
                  <span className="hud-items">
                    {shown.map((w, k) => (
                      <WeaponIcon key={`${w}${k}`} weapon={w} className={`hud-item is-${itemSlot(w)}${w === v?.weapon ? " is-held" : ""}`} />
                    ))}
                  </span>
                  {isAlive && v!.bomb && <Cs2Icon name="c4" label={t("demos.hud.bomb")} className="hud-gear is-bomb" />}
                  {isAlive && v!.kit && side === "ct" && <Cs2Icon name="defuser" label={t("demos.viewer.kit")} className="hud-gear" />}
                  <span className="hud-kd mono" title={t("demos.hud.kdHelp")}>
                    {row?.kills ?? 0}/{row?.deaths ?? 0}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** How long a kill stays in the feed, in game seconds. */
const FEED_SECONDS = 6;

export function KillFeed({ replay, round, tick, numbers, followed }: { replay: Replay; round: RoundIndex; tick: number; numbers: number[]; followed: number | null }) {
  const { t } = useTranslation();
  const recent = round.kills.filter((k) => k.tick <= tick && tick - k.tick <= FEED_SECONDS * replay.tickRate).slice(-5);
  const sideOf = (i: number) => (i >= 0 && replay.players[i]?.team === round.round.tTeam ? "t" : "ct");
  const name = (i: number) => (i >= 0 ? (replay.players[i]?.name ?? "") : t("demos.viewer.world"));
  return (
    <ol className="hud-feed" aria-label={t("demos.hud.feed")} aria-live="off">
      {recent.map((k) => (
        <li key={`${k.tick}-${k.victim}`} className={`hud-kill${followed !== null && (k.killer === followed || k.victim === followed) ? " is-mine" : ""}`}>
          <KillParts k={k} name={name} sideOf={sideOf} numbers={numbers} />
        </li>
      ))}
    </ol>
  );
}

/** A kill as the game writes it: killer (and assist), weapon and how, victim. Also used by Round events. */
export function KillParts({ k, name, sideOf, numbers }: { k: Replay["kills"][number]; name: (i: number) => string; sideOf: (i: number) => "t" | "ct"; numbers: number[] }) {
  const { t } = useTranslation();
  const suicide = k.killer === k.victim || k.killer < 0;
  return (
    <span className="kill-parts">
      {!suicide && (
        <span className={`kill-who ${SIDE_CLASS[sideOf(k.killer)]}`}>
          <span className="demo-num-mini">{numbers[k.killer]}</span>
          {name(k.killer)}
        </span>
      )}
      {k.assister >= 0 && k.assister !== k.killer && (
        <span className={`kill-assist ${SIDE_CLASS[sideOf(k.assister)]}`}>
          +{k.flashAssist ? <Cs2Icon name="flashbang_assist" label={t("demos.hud.flashAssist")} className="kill-mark" /> : null}
          {name(k.assister)}
        </span>
      )}
      {k.blind && <Cs2Icon name="blind" label={t("demos.hud.blindKill")} className="kill-mark" />}
      {k.air && <Cs2Icon name="air" label={t("demos.hud.airKill")} className="kill-mark" />}
      {suicide ? <Cs2Icon name="suicide" label={t("demos.hud.suicide")} className="kill-weapon" /> : <WeaponIcon weapon={k.weapon} className="kill-weapon" />}
      {k.noScope && <Cs2Icon name="noscope" label={t("demos.hud.noScope")} className="kill-mark" />}
      {k.smoke && <Cs2Icon name="through_smoke" label={t("demos.viewer.throughSmoke")} className="kill-mark" />}
      {k.wallbang && <Cs2Icon name="wallbang" label={t("demos.viewer.wallbang")} className="kill-mark" />}
      {k.headshot && <Cs2Icon name="headshot" label={t("demos.viewer.headshot")} className="kill-mark" />}
      <span className={`kill-who is-victim ${SIDE_CLASS[sideOf(k.victim)]}`}>{name(k.victim)}</span>
    </span>
  );
}

/** One button for the speed; the choices open under it. */
export function SpeedMenu({ speeds, speed, onChange }: { speeds: readonly number[]; speed: number; onChange: (s: number) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    const key = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", key, true);
    // The chosen speed takes the focus, so the arrows move from there.
    box.current?.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus();
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", key, true);
    };
  }, [open]);
  const move = (e: React.KeyboardEvent, i: number) => {
    const d = e.key === "ArrowDown" ? 1 : e.key === "ArrowUp" ? -1 : 0;
    if (!d) return;
    e.preventDefault();
    const items = box.current?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]');
    items?.[(i + d + speeds.length) % speeds.length]?.focus();
  };
  return (
    <div className="speed-menu" ref={box}>
      <button
        ref={button}
        type="button"
        className="btn btn-secondary btn-small speed-button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={id}
        aria-label={t("demos.hud.speed", { speed })}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="mono">{speed}×</span>
        <span aria-hidden="true">▾</span>
      </button>
      {open && (
        <div id={id} role="menu" className="speed-list" aria-label={t("player.speed")}>
          {speeds.map((s, i) => (
            <button
              key={s}
              type="button"
              role="menuitemradio"
              aria-checked={s === speed}
              className="speed-item mono"
              onKeyDown={(e) => move(e, i)}
              onClick={() => {
                onChange(s);
                setOpen(false);
                button.current?.focus();
              }}
            >
              {s}×
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
